import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, Bookmark } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { notify } from "../lib/notify";
import CSRSGauge from "../components/CSRSGauge";
import ShareCard from "../components/ShareCard";
import { PDPAScorer, MASTRMReadiness, AIRiskMaturity, NonComplianceCost, MASCalendar } from "./Wizards";

// --- CSRS Calculator ---
function CSRSCalculator() {
  const { user } = useAuth();
  const [values, setValues] = useState({
    mas_trm_compliance: 70,
    pdpa_compliance: 65,
    cyber_hygiene: 80,
    third_party_risk: 60,
    ai_governance: 50,
    operational_resilience: 75,
  });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showShare, setShowShare] = useState(false);

  const setVal = (k, v) => setValues(prev => ({ ...prev, [k]: Number(v) }));

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/csrs", values);
      setResult(r.data);
      setShowShare(true);
    } finally { setBusy(false); }
  };

  const save = async () => {
    if (!user) { notify.info("Sign in to save your assessment."); return; }
    try {
      await api.post("/assessments", { type: "csrs", title: `CSRS ${result.score}/100 · ${result.band}`, input: values, result });
      notify.success("Saved to your account.");
    } catch { notify.error("Could not save."); }
  };

  const labels = {
    mas_trm_compliance: "MAS TRM Compliance",
    pdpa_compliance: "PDPA Compliance",
    cyber_hygiene: "Cyber Hygiene (MAS 655)",
    third_party_risk: "Third-Party Risk",
    ai_governance: "AI Governance (MindForge)",
    operational_resilience: "Operational Resilience",
  };

  return (
    <div data-testid="tool-csrs">
      <ToolHero
        overline="Flagship Tool"
        title="Composite Singapore Risk Score"
        sub="CSRS combines six Singapore-specific risk signals into one 0-100 metric. Move the sliders to reflect your honest current state."
      />

      <section className="section">
        <div className="container-rp grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 space-y-6">
            {Object.keys(labels).map(k => (
              <div key={k} className="rp-card p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="overline-muted">Domain</div>
                    <div className="font-display text-lg font-semibold mt-1">{labels[k]}</div>
                  </div>
                  <div className="font-mono text-2xl font-bold" style={{ color: "var(--rp-primary)" }}>{values[k]}</div>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="1"
                  value={values[k]}
                  onChange={(e) => setVal(k, e.target.value)}
                  className="w-full mt-4 accent-slate-900"
                  data-testid={`csrs-slider-${k}`}
                />
                <div className="flex justify-between text-[10px] font-mono text-slate-400 mt-1">
                  <span>At Risk</span><span>Adequate</span><span>Strong</span>
                </div>
              </div>
            ))}
            <button onClick={submit} disabled={busy} className="btn-accent" data-testid="csrs-calculate">
              {busy ? "Calculating…" : "Calculate my CSRS"} <ArrowRight size={14} />
            </button>
          </div>

          <aside className="lg:col-span-5">
            <div className="rp-card p-8 sticky top-24" data-testid="csrs-result">
              <div className="overline">{result ? "Your CSRS" : "Score preview"}</div>
              <h3 className="font-display text-2xl font-semibold mt-2">{result ? `${result.band}` : "Run the calculation"}</h3>
              <div className="flex justify-center my-6">
                <CSRSGauge score={result ? result.score : 0} band={result ? result.band : "—"} color={result ? result.color : "rose"} size={300} />
              </div>
              {result && (
                <>
                  <p className="text-sm text-slate-600 leading-relaxed">{result.narrative}</p>
                  <div className="mt-6">
                    <div className="overline-muted mb-3">Breakdown</div>
                    <div className="space-y-2">
                      {result.breakdown.map(b => (
                        <div key={b.domain} className="flex items-center gap-3 text-sm">
                          <span className="flex-1">{b.domain}</span>
                          <span className="font-mono text-xs text-slate-400">{b.weight}</span>
                          <span className="font-mono font-semibold w-10 text-right">{b.score}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <Link to="/tools/framework-matcher" className="btn-primary mt-6 w-full justify-center">Find your best framework <ArrowRight size={14} /></Link>
                  <div className="mt-6 pt-6 border-t border-slate-200">
                    <div className="overline-muted mb-3">Share your score</div>
                    <ShareCard
                      title="Composite Singapore Risk Score"
                      subtitle="MAS TRM · PDPA · MindForge · Resilience"
                      score={result.score}
                      band={result.band}
                      color={result.color}
                      extras={result.breakdown.map(b => ({ label: b.domain, value: `${b.score}/100` }))}
                      onSave={user ? save : null}
                    />
                    {!user && <p className="text-[11px] text-slate-500 mt-3"><Link to="/login" className="font-medium" style={{ color: "var(--rp-accent)" }}>Sign in</Link> to save this assessment to your account.</p>}
                  </div>
                </>
              )}
              {!result && <p className="text-sm text-slate-500">Move the sliders and click <strong>Calculate</strong> to see your composite score with breakdown and narrative.</p>}
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

// --- Framework Matcher ---
function FrameworkMatcher() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({ industry: "", maturity: "", primary_goal: "", risk_focus: "", org_size: "" });
  const [matches, setMatches] = useState(null);
  const [busy, setBusy] = useState(false);

  const Q = [
    { key: "industry", label: "What industry are you in?", options: [
      ["banking", "Banking · NBFC"], ["insurance", "Insurance"], ["capital_markets", "Capital Markets"], ["fintech", "Fintech"], ["payments", "Payments / Crypto"], ["other", "Other"]
    ]},
    { key: "maturity", label: "How would you describe your current risk maturity?", options: [
      ["initial", "Initial · ad-hoc"], ["developing", "Developing"], ["defined", "Defined"], ["managed", "Managed"], ["optimising", "Optimising"]
    ]},
    { key: "primary_goal", label: "What's your primary goal for the next 12 months?", options: [
      ["certification", "Achieve certification"], ["regulatory_compliance", "Regulatory compliance"], ["risk_visibility", "Board / risk visibility"], ["operational_efficiency", "Operational efficiency"]
    ]},
    { key: "risk_focus", label: "Which risk area are you most focused on?", options: [
      ["cyber", "Cyber & IT"], ["financial", "Financial / Capital"], ["operational", "Operational"], ["ai", "AI risk"], ["esg", "ESG / Climate"]
    ]},
    { key: "org_size", label: "What's your organisation size?", options: [
      ["small", "Small (< 100)"], ["medium", "Medium (100–500)"], ["large", "Large (500–5000)"], ["enterprise", "Enterprise (5000+)"]
    ]},
  ];

  const pick = (key, v) => {
    setData(prev => ({ ...prev, [key]: v }));
    if (step < Q.length - 1) setStep(step + 1);
    else submit({ ...data, [key]: v });
  };

  const submit = async (final) => {
    setBusy(true);
    try {
      const r = await api.post("/framework-matcher", final);
      setMatches(r.data.matches);
    } finally { setBusy(false); }
  };

  if (matches) return (
    <div data-testid="tool-matcher-result">
      <ToolHero overline="Your matches" title="Your top 4 frameworks for Singapore." sub="Ranked by match score against your profile." />
      <section className="section">
        <div className="container-rp">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
            {matches.map((m, i) => (
              <Link to={`/frameworks/${m.slug}`} key={m.slug} className="bg-white p-6 hover:bg-slate-50 transition-colors flex flex-col" data-testid={`match-${m.slug}`}>
                <div className="flex items-center justify-between">
                  <div className="font-mono text-xs text-slate-400">#{i + 1}</div>
                  <div className="font-mono text-2xl font-bold" style={{ color: "var(--rp-accent)" }}>{m.match_score}</div>
                </div>
                <div className="font-display text-xl font-semibold mt-3">{m.name}</div>
                <div className="text-xs text-slate-500 mt-1">{m.tagline}</div>
                <p className="text-sm text-slate-600 mt-4 leading-relaxed flex-1 line-clamp-4">{m.description}</p>
                <div className="text-xs font-medium mt-4" style={{ color: "var(--rp-accent)" }}>Open detail →</div>
              </Link>
            ))}
          </div>
          <div className="mt-10 flex gap-3 flex-wrap">
            <button onClick={() => { setMatches(null); setStep(0); setData({}); }} className="btn-outline">Run again</button>
            <Link to="/tools/csrs-calculator" className="btn-primary">Now score with CSRS <ArrowRight size={14} /></Link>
          </div>
        </div>
      </section>
    </div>
  );

  const cur = Q[step];
  return (
    <div data-testid="tool-matcher">
      <ToolHero overline="5-Question Framework Matcher" title={`Question ${step + 1} of ${Q.length}`} sub={cur.label} />
      <section className="section">
        <div className="container-rp max-w-3xl">
          <div className="flex gap-1.5 mb-8">
            {Q.map((_, i) => <div key={i} className={`h-1 flex-1 ${i <= step ? "bg-slate-900" : "bg-slate-200"}`} />)}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {cur.options.map(([val, label]) => (
              <button
                key={val}
                onClick={() => pick(cur.key, val)}
                className="text-left rp-card p-5 hover:border-slate-400"
                disabled={busy}
                data-testid={`matcher-opt-${val}`}
              >
                <div className="font-display text-lg font-semibold">{label}</div>
              </button>
            ))}
          </div>
          {step > 0 && (
            <button onClick={() => setStep(step - 1)} className="mt-6 text-xs text-slate-500 hover:text-slate-900" data-testid="matcher-back">← Back</button>
          )}
        </div>
      </section>
    </div>
  );
}

// --- PDPA Quick Quiz ---
const PDPA_QUIZ = [
  { q: "Within how many hours must a significant PDPA breach be notified to the PDPC?", o: ["24 hours", "48 hours", "72 hours", "7 days"], a: 2 },
  { q: "What is the maximum financial penalty under PDPA (post-Oct 2022)?", o: ["S$100,000", "S$1 million only", "10% of annual turnover or S$1m (whichever higher)", "S$5 million flat"], a: 2 },
  { q: "Which obligation requires you to inform individuals of purpose before collection?", o: ["Notification", "Consent", "Accuracy", "Purpose Limitation"], a: 0 },
  { q: "Is appointing a Data Protection Officer (DPO) mandatory for Singapore organisations?", o: ["Yes for all", "Only for FIs", "Only for SDFs", "No, only voluntary"], a: 0 },
  { q: "Mandatory breach notification applies if data of how many individuals is affected (significant scale)?", o: ["100+", "500+", "1,000+", "10,000+"], a: 1 },
];

function PDPAQuiz() {
  const [step, setStep] = useState(0);
  const [score, setScore] = useState(0);
  const [picked, setPicked] = useState(null);
  const [done, setDone] = useState(false);

  if (done) {
    const pct = Math.round((score / PDPA_QUIZ.length) * 100);
    return (
      <div data-testid="tool-pdpa-quiz-result">
        <ToolHero overline="PDPA Quick Quiz · Result" title={`${score}/${PDPA_QUIZ.length} correct`} sub={pct >= 80 ? "Strong PDPA awareness." : pct >= 60 ? "Solid — but a few gaps." : "Time for a refresher."} />
        <section className="section">
          <div className="container-rp max-w-3xl">
            <div className="rp-card p-8">
              <div className="font-display text-6xl font-bold" style={{ color: "var(--rp-accent)" }}>{pct}%</div>
              <p className="text-slate-600 mt-4">Share with your team or take a deeper PDPA assessment for a remediation roadmap.</p>
              <div className="mt-6 flex gap-3 flex-wrap">
                <Link to="/tools/pdpa-scorer" className="btn-accent">Take the 10-question PDPA Scorer <ArrowRight size={14} /></Link>
                <button className="btn-outline" onClick={() => { setStep(0); setScore(0); setPicked(null); setDone(false); }}>Retake</button>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const q = PDPA_QUIZ[step];
  const submitChoice = () => {
    if (picked == null) return;
    if (picked === q.a) setScore(s => s + 1);
    if (step < PDPA_QUIZ.length - 1) { setStep(step + 1); setPicked(null); }
    else setDone(true);
  };

  return (
    <div data-testid="tool-pdpa-quiz">
      <ToolHero overline={`PDPA Quick Quiz · Question ${step + 1} of ${PDPA_QUIZ.length}`} title={q.q} sub="Pick the best answer. 60 seconds total." />
      <section className="section">
        <div className="container-rp max-w-3xl">
          <div className="flex gap-1.5 mb-8">
            {PDPA_QUIZ.map((_, i) => <div key={i} className={`h-1 flex-1 ${i <= step ? "bg-slate-900" : "bg-slate-200"}`} />)}
          </div>
          <div className="grid grid-cols-1 gap-3">
            {q.o.map((opt, i) => (
              <button
                key={opt}
                onClick={() => setPicked(i)}
                data-active={picked === i}
                className="persona-btn p-5 justify-start text-left"
                data-testid={`quiz-opt-${i}`}
              >
                <span className="font-mono mr-3 text-xs">{String.fromCharCode(65 + i)}</span>
                <span className="font-medium">{opt}</span>
              </button>
            ))}
          </div>
          <button onClick={submitChoice} disabled={picked == null} className="btn-accent mt-8 disabled:opacity-40" data-testid="quiz-next">
            {step === PDPA_QUIZ.length - 1 ? "See result" : "Next"} <ArrowRight size={14} />
          </button>
        </div>
      </section>
    </div>
  );
}

// --- Generic placeholder for other tools ---
function GenericTool({ slug, name, description }) {
  return (
    <div data-testid={`tool-${slug}`}>
      <ToolHero overline="Tool" title={name} sub={description || "Detailed multi-step assessor."} />
      <section className="section">
        <div className="container-rp max-w-3xl">
          <div className="rp-card p-10 text-center">
            <div className="overline">Coming soon</div>
            <h2 className="font-display text-3xl font-semibold mt-3">This assessor is being prepared.</h2>
            <p className="text-slate-600 mt-3">We're scoping the questions with the MAS / PDPC / CSA control catalogues before launching. In the meantime, try one of our live tools.</p>
            <div className="mt-6 flex justify-center gap-3 flex-wrap">
              <Link to="/tools/csrs-calculator" className="btn-accent">CSRS Calculator</Link>
              <Link to="/tools/framework-matcher" className="btn-outline">Framework Matcher</Link>
              <Link to="/tools/pdpa-quiz" className="btn-outline">PDPA Quiz</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function ToolHero({ overline, title, sub }) {
  return (
    <section className="border-b border-slate-200">
      <div className="container-rp py-12 lg:py-16">
        <div className="overline">{overline}</div>
        <h1 className="font-display text-4xl lg:text-5xl font-bold mt-3 tracking-tight">{title}</h1>
        {sub && <p className="text-slate-600 mt-4 max-w-2xl text-lg">{sub}</p>}
      </div>
    </section>
  );
}

export default function ToolDetail() {
  const { slug } = useParams();
  if (slug === "csrs-calculator") return <CSRSCalculator />;
  if (slug === "framework-matcher") return <FrameworkMatcher />;
  if (slug === "pdpa-quiz") return <PDPAQuiz />;
  if (slug === "pdpa-scorer") return <PDPAScorer />;
  if (slug === "mas-trm-readiness") return <MASTRMReadiness />;
  if (slug === "ai-risk-maturity") return <AIRiskMaturity />;
  if (slug === "non-compliance-cost") return <NonComplianceCost />;
  if (slug === "mas-calendar") return <MASCalendar />;

  const map = {};
  const meta = map[slug] || { name: "Tool", desc: "" };
  return <GenericTool slug={slug} name={meta.name} description={meta.desc} />;
}
